"use client";

import { useEffect, useRef, useState } from "react";

interface ServedHaircutsCounterProps {
  /** Final value to count up to (total haircuts served). */
  count: number;
  /** Animation duration in milliseconds. */
  durationMs?: number;
}

/**
 * Animated "haircuts served" counter. Counts up from 0 to `count` the first
 * time it scrolls into view, then holds the final value.
 */
const ServedHaircutsCounter = ({
  count,
  durationMs = 1600,
}: ServedHaircutsCounterProps) => {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const runAnimation = () => {
      if (hasAnimated.current) return;
      hasAnimated.current = true;

      const start = performance.now();
      const step = (now: number) => {
        const progress = Math.min((now - start) / durationMs, 1);
        // easeOutCubic for a natural deceleration.
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplayValue(Math.round(eased * count));
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            runAnimation();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [count, durationMs]);

  return (
    <span ref={ref} aria-label={`${count.toLocaleString()} haircuts served`}>
      {displayValue.toLocaleString()}
    </span>
  );
};

export default ServedHaircutsCounter;
